/**
 * Sort Folder Action
 *
 * Sorts a folder's contents (or the entire drive, when invoked on the root via
 * an empty-space right-click) into folders using deterministic-first placement,
 * falling back to the LLM only where no deterministic answer applies (#10846).
 *
 * The action is async: it enqueues the sort on the backend and hands the task
 * id to the filetree hook, which polls for progress and shows a persistent
 * "Sorting X of N…" indicator. Before enqueuing it fetches an estimate so the
 * confirmation modal can show how many files will move — the guard against an
 * accidental massive sort.
 */

import { Datum } from "../datastructure";
import { ActionDefinition, ActionContext, toApiPath } from "./types";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL_V2 } from "@/constants";
import { isReadOnlyDatum } from "../specialFolders";

interface SortEstimate {
  count: number;
  exceedsLimit: boolean;
}

export async function sortFolder(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  if (datum.type !== "folder") {
    context.showToast?.("Can only sort folders", "error");
    return;
  }

  const apiPath = toApiPath(datum.path, context.filetree);
  const isRoot = apiPath.length === 0;
  const label = isRoot ? "this drive" : `"${datum.name}"`;

  // Estimate first so the confirmation can surface the file count. Fall back to
  // an unknown count rather than blocking the sort if the estimate fails.
  let estimate: SortEstimate | null = null;
  try {
    const estResp = await fpFetch(
      `${BASE_API_URL_V2}/api/v2/tasks/sort/estimate`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path: apiPath }),
      }
    );
    if (estResp.ok) {
      estimate = await estResp.json();
    }
  } catch {
    estimate = null;
  }

  if (estimate && estimate.count === 0) {
    context.showToast?.(`No files to sort in ${label}`, "info");
    return;
  }

  const countText =
    estimate != null
      ? `${estimate.count} file${estimate.count === 1 ? "" : "s"}`
      : "all files";
  const largeWarning = estimate?.exceedsLimit
    ? " This is a large sort and may take a while."
    : "";

  const confirmed = context.showConfirmation
    ? await context.showConfirmation({
        title: isRoot ? "Sort this drive" : "Sort folder",
        message: `This will automatically reorganize ${countText} in ${label} into folders.${largeWarning} Continue?`,
        confirmText: "Sort",
      })
    : true;
  if (!confirmed) return;

  try {
    const response = await fpFetch(`${BASE_API_URL_V2}/api/v2/tasks/sort`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        path: apiPath,
        acknowledge_large: estimate?.exceedsLimit ?? false,
      }),
    });

    if (!response.ok) {
      context.showToast?.(`Failed to start sorting ${label}`, "error");
      return;
    }

    const body: { taskId?: string; count?: number } = await response.json();
    if (body.taskId && context.onSortStarted) {
      // The hook owns the progress banner + polling from here.
      context.onSortStarted(body.taskId, body.count ?? estimate?.count ?? 0);
    } else {
      // No progress channel available (e.g. selection surfaces): fall back to a
      // one-shot refresh once the request returns.
      context.showToast?.(`Sorting ${label}…`, "info");
      context.refreshTree();
    }
  } catch (error) {
    console.error("sortFolder: Failed to sort folder", error);
    context.showToast?.(`Failed to sort ${label}`, "error");
  }
}

/**
 * Action definition for context menu
 * Note: This should only be shown to admin users (filtered in
 * getContextMenuActions).
 */
export const sortFolderAction: ActionDefinition = {
  config: {
    action: "sort-folder",
    label: "Sort",
  },
  // Read-only / connector-managed trees (SharePoint, OneNote, Teams, Box,
  // client drives) are never sortable — moving their files out fights the sync
  // engine that owns them. The backend enforces this too; this just keeps the
  // action off the menu. See specialFolders.ts and folder_sort._is_sort_excluded.
  //
  // A confirmation channel is required, not optional: without one this action
  // enqueues the sort immediately AND auto-sets `acknowledge_large`, which is
  // the client half of the SORT_MAX_BLOBS guard (#10846). Surfaces that cannot
  // prompt (search results — the modal lives inside the tree render) must not
  // offer it at all rather than offer an unconfirmable bulk reorganization.
  isApplicable: (datum, context) =>
    datum.type === "folder" &&
    Boolean(context?.showConfirmation) &&
    !isReadOnlyDatum(datum, {
      readOnlyDriveNames: context?.readOnlyDriveNames,
      sftpDriveNames: context?.sftpDriveNames,
      sharepointRootNames: context?.sharepointRootNames,
      isAcrossDrivesMode: context?.isAcrossDrivesMode,
    }),
  execute: sortFolder,
};
